import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { logEvent } from "../../services/evalMode.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { Check, Crown, Sparkles } from "lucide-react";
import SubscriptionButton from "../elements/subscriptionButton.tsx";
import { STRIPE_CONFIG } from "../../config/stripe";
import { BILLING } from "../../config/billing";
import { logTrialCtaShown } from "../../analytics/onboarding.ts";
import {
  analytics,
  auth,
  db,
  ensureRemoteConfig,
  getUserPlanData,
  remoteConfig,
} from "../../services/firebase";
import { createDesignFromTemplate } from "../../services/createDesignFromTemplate";
import {
  PENDING_TEMPLATE_STORAGE_KEY,
  templateIdFromTemplatesPath,
} from "../../utils/navigation";
import { EDITOR_ONBOARDING_STORAGE_COMPLETED } from "../onboarding/editorOnboardingSteps.tsx";
import { logActivationDrillCreated } from "../../analytics/onboarding.ts";
import {
  ROLE_UI_OPTIONS,
  UI_LABEL_TO_ROLE,
  getDefaultTemplateIdForRole,
  isBeginnerPriorExperience,
  isLargeEnsembleSize,
  type OnboardingRole,
} from "./onboardingFlowConfig.ts";

/** Collected answers — matches Firestore fields written by the legacy onboarding steps. */
export type DrillFloOnboardingData = {
  userType: string;
  onboardingRole: OnboardingRole | null;
  typicalPerformerCount: string;
  priorExperience: string;
  designTools: Array<
    "pyware" | "envision" | "openmarch" | "micromarchingleague" | "paper"
  >;
  showTitle: string;
  showDate: string;
  firstName?: string;
  lastName?: string;
  entryMode: "template" | "blank" | null;
};

function buildEditorUrl(
  drillId: string,
  opts: {
    tutorial: boolean;
    onboardingRole: string | null;
    trial: boolean;
    priorExperience?: string | null;
    typicalPerformerCount?: string | null;
  },
) {
  const q = new URLSearchParams();
  if (opts.tutorial) q.set("tutorial", "1");
  if (opts.onboardingRole) q.set("onboarding_role", opts.onboardingRole);
  if (opts.trial) q.set("onboarding_trial", "1");
  if (isBeginnerPriorExperience(opts.priorExperience)) {
    q.set("onboarding_experience", "beginner");
  }
  if (isLargeEnsembleSize(opts.typicalPerformerCount)) {
    q.set("onboarding_ensemble", "large");
  }
  const s = q.toString();
  return `/designs/${drillId}${s ? `?${s}` : ""}`;
}

const TOTAL_STEPS = 9;
const TRIAL_LENGTH = parseInt(
  process.env.REACT_APP_DRILLFLO_PRO_TRIAL_LENGTH || String(BILLING.trialDays),
  10,
);
/** Each step panel width as % of the horizontal flex track (`TOTAL_STEPS * 100%` wide). */
const STEP_PANEL_WIDTH_PCT = 100 / TOTAL_STEPS;
const FLO_BLUE = "#2E5BFF";
const FLO_BLUE_HOVER = "#2A54EB";

const METHOD_OPTIONS = [
  { id: "pyware" as const, label: "Pyware" },
  { id: "envision" as const, label: "EnVision" },
  { id: "openmarch" as const, label: "OpenMarch" },
  { id: "micromarchingleague" as const, label: "Micro Marching League" },
  { id: "paper" as const, label: "Paper / Spreadsheet" },
  { id: "none" as const, label: "None" },
];

/** Same buckets as `PreferencesStep.tsx` */
const PERFORMER_OPTIONS = ["20-50", "50-100", "100-200", "200+"] as const;

/** Same copy as `experienceStep.tsx` (stored in `experience` / `onboardingProfile.priorExperience`) */
const PRIOR_SOFTWARE_OPTIONS = ["Yes, I have", "No, I'm new to this"] as const;

function cn(...parts: Array<string | false | undefined | null>) {
  return parts.filter(Boolean).join(" ");
}

function IconUser({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 12a4 4 0 100-8 4 4 0 000 8zM4 20a8 8 0 0116 0"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconUsersEnsemble({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="15" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4 18c0-2.5 2-4.5 5-4.5s5 2 5 4.5M10 18c0-2.5 2-4.5 5-4.5s5 2 5 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconCheck({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 12l2.5 2.5L16 9"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconCircleOff({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 8l8 8M16 8l-8 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FieldGridBackdrop() {
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
    >
      <svg
        className="flo-field-drift h-[120%] w-[120%] -translate-x-[10%] -translate-y-[10%] opacity-[0.14]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="yardlines"
            width="56"
            height="56"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M0 0h56M0 28h56"
              stroke="#94a3b8"
              strokeWidth="0.5"
              opacity="0.9"
            />
            <path
              d="M28 0v56"
              stroke="#cbd5e1"
              strokeWidth="0.35"
              opacity="0.85"
            />
          </pattern>
          <linearGradient id="fieldFade" x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#FDFCFB" stopOpacity="0" offset="0%" />
            <stop stopColor="#FDFCFB" stopOpacity="0.85" offset="55%" />
            <stop stopColor="#FDFCFB" stopOpacity="1" offset="100%" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#yardlines)" />
        <rect width="100%" height="100%" fill="url(#fieldFade)" />
      </svg>
      <div className="flo-vignette pointer-events-none absolute inset-0" />
    </div>
  );
}

function Wordmark({
  className,
  reveal,
}: {
  className?: string;
  reveal?: boolean;
}) {
  return (
    <div
      className={cn(
        "font-fraunces text-[clamp(2.5rem,9vw,4.25rem)] font-semibold tracking-tight text-slate-900",
        reveal && "flo-wordmark-reveal",
        className,
      )}
    >
      <span className="inline-block">Drill</span>
      <span className="inline-block text-flo-blue">Flo</span>
    </div>
  );
}

function FloAvatar() {
  return (
    <div
      className="relative mx-auto flex aspect-square w-full max-w-[min(100%,280px)] items-center justify-center"
      aria-hidden
    >
      <div className="flo-avatar-glow absolute inset-[12%] rounded-full blur-2xl" />
      <svg
        viewBox="0 0 200 200"
        className="relative h-full w-full text-flo-blue"
      >
        <defs>
          <radialGradient id="floCore" cx="50%" cy="45%" r="55%">
            <stop offset="0%" stopColor="#2E5BFF" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#2E5BFF" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="floArc" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2E5BFF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#64748b" stopOpacity="0.35" />
          </linearGradient>
        </defs>
        <circle cx="100" cy="100" r="78" fill="url(#floCore)" />
        {[0, 22, 44, 66, 88].map((y, i) => (
          <path
            key={y}
            d={`M 20 ${80 + y} Q 100 ${60 + y * 0.4} 180 ${80 + y}`}
            fill="none"
            stroke="url(#floArc)"
            strokeWidth={i === 2 ? 2.2 : 1.2}
            strokeOpacity={0.45 + i * 0.08}
            className="flo-avatar-line"
            style={{ animationDelay: `${i * 0.12}s` }}
          />
        ))}
        <g opacity="0.5">
          <line
            x1="40"
            y1="100"
            x2="160"
            y2="100"
            stroke="#2E5BFF"
            strokeWidth="0.75"
          />
          <line
            x1="100"
            y1="40"
            x2="100"
            y2="160"
            stroke="#94a3b8"
            strokeWidth="0.6"
          />
        </g>
        <circle
          cx="100"
          cy="100"
          r="6"
          fill="#2E5BFF"
          className="flo-avatar-pulse"
        />
      </svg>
    </div>
  );
}

const ONBOARDING_STYLES = `
@keyframes flo-enter-up {
  from { opacity: 0; transform: translateY(16px) scale(0.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes flo-field-drift {
  0% { transform: translate(0, 0); }
  100% { transform: translate(-3%, -2%); }
}
@keyframes flo-wordmark-in {
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes flo-spring-in {
  0% { opacity: 0; transform: scale(0.88); }
  60% { opacity: 1; transform: scale(1.04); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes flo-pulse-soft {
  0%, 100% { box-shadow: 0 0 0 0 rgba(46, 91, 255, 0.35); }
  50% { box-shadow: 0 0 0 6px rgba(46, 91, 255, 0.08); }
}
@keyframes flo-avatar-pulse {
  0%, 100% { opacity: 0.85; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}
.flo-enter-item {
  opacity: 0;
  animation: flo-enter-up 380ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
@media (prefers-reduced-motion: reduce) {
  .flo-enter-item { animation: none; opacity: 1; transform: none; }
  .flo-field-drift { animation: none !important; }
  .flo-wordmark-reveal { animation: none !important; opacity: 1 !important; }
  .flo-spring-mark { animation: none !important; }
  .flo-avatar-pulse, .flo-avatar-line { animation: none !important; }
}
.flo-field-drift { animation: flo-field-drift 48s linear infinite; }
.flo-wordmark-reveal { animation: flo-wordmark-in 400ms ease-out forwards; opacity: 0; }
.flo-vignette {
  background: radial-gradient(ellipse at 50% 40%, transparent 30%, #FDFCFB 72%);
}
.flo-avatar-glow {
  background: radial-gradient(circle at 50% 45%, rgba(46,91,255,0.35), transparent 65%);
  animation: flo-pulse-soft 4s ease-in-out infinite;
}
.flo-avatar-pulse { animation: flo-avatar-pulse 3.2s ease-in-out infinite; }
.flo-avatar-line {
  stroke-dasharray: 8 10;
  animation: flo-dash 6s linear infinite;
}
@keyframes flo-dash {
  to { stroke-dashoffset: -120; }
}
@media (prefers-reduced-motion: reduce) {
  .flo-avatar-line { animation: none; }
}
.flo-spring-mark { animation: flo-spring-in 640ms cubic-bezier(0.34, 1.4, 0.64, 1) both; }
`;

function useOnboardingFonts() {
  useEffect(() => {
    const fraunces = document.createElement("link");
    fraunces.rel = "stylesheet";
    fraunces.href =
      "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400&display=swap";
    document.head.appendChild(fraunces);

    const style = document.createElement("style");
    style.setAttribute("data-drillflo-onboarding-fonts", "true");
    style.textContent = `
@font-face {
  font-family: 'Geist';
  src: url('https://cdn.jsdelivr.net/npm/geist@1.3.1/dist/fonts/geist-sans/Geist-Variable.woff2') format('woff2');
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Geist Mono';
  src: url('https://cdn.jsdelivr.net/npm/geist@1.3.1/dist/fonts/geist-mono/GeistMono-Variable.woff2') format('woff2');
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}
`;
    document.head.appendChild(style);

    return () => {
      fraunces.remove();
      style.remove();
    };
  }, []);
}

function useOnboardingUserDocId() {
  const [userDocId, setUserDocId] = useState<string | null>(null);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUserDocId(null);
        return;
      }
      try {
        const q = query(collection(db, "users"), where("uid", "==", user.uid));
        const snap = await getDocs(q);
        if (!snap.empty) setUserDocId(snap.docs[0].id);
        else setUserDocId(null);
      } catch (e) {
        console.error("DrillFlo onboarding: user lookup failed", e);
        setUserDocId(null);
      }
    });
    return () => unsub();
  }, []);
  return userDocId;
}

export function DrillFloOnboardingExperience({
  onComplete,
  className,
  experimentVariant,
  flowId = "flo_experience_v1",
  experimentName = "onboarding_flow_v1",
  fastPath = false,
}: {
  onComplete?: (data: DrillFloOnboardingData) => void;
  className?: string;
  /** Same as `AccountSetupStep` — stored on `users` when set. */
  experimentVariant?: string;
  /** Analytics / user doc — match `OnboardingLayout` / `account-setup`. */
  flowId?: string;
  experimentName?: string;
  fastPath?: boolean;
}) {
  useOnboardingFonts();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userDocId = useOnboardingUserDocId();
  const stepRegionId = useId();
  const [step, setStep] = useState(0);
  const [offerBilling, setOfferBilling] = useState<"monthly" | "annual">(
    "monthly",
  );
  const [user] = useAuthState(auth);
  const exclusiveOfferViewLogged = useRef(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const pendingFirstDesignIdRef = useRef<string | null>(null);
  const setPendingId = useCallback((id: string | null) => {
    pendingFirstDesignIdRef.current = id;
  }, []);
  const [data, setData] = useState<DrillFloOnboardingData>({
    userType: "",
    onboardingRole: null,
    typicalPerformerCount: "",
    priorExperience: "",
    designTools: [],
    showTitle: "",
    showDate: "",
    firstName: "",
    lastName: "",
    entryMode: null,
  });
  const [profileHydrated, setProfileHydrated] = useState(false);

  const panelRefs = useRef<Array<HTMLElement | null>>([]);
  const setPanelRef = useCallback((el: HTMLElement | null, index: number) => {
    panelRefs.current[index] = el;
  }, []);

  /** Hydrate from Firestore (parity with legacy steps reading `users`). */
  useEffect(() => {
    if (!userDocId) {
      setProfileHydrated(true);
      return;
    }
    setProfileHydrated(false);
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", userDocId));
        if (!snap.exists()) return;
        const d = snap.data() as {
          firstName?: string;
          lastName?: string;
          userType?: string;
          onboardingRole?: OnboardingRole;
          attribution?: { landing_role_intent?: string };
          typicalPerformerCount?: string;
          experience?: string;
          pendingFirstDesignId?: string;
          onboardingProfile?: {
            typicalPerformerCount?: string;
            priorExperience?: string;
            designTools?: string[];
            firstShowTitle?: string;
            firstShowDate?: string;
          };
        };
        let userType = d.userType || "";
        if (!userType && d.attribution?.landing_role_intent) {
          const intent = d.attribution.landing_role_intent;
          const match = ROLE_UI_OPTIONS.find(
            (label) => UI_LABEL_TO_ROLE[label] === intent,
          );
          if (match) userType = match;
        }
        const perf =
          d.typicalPerformerCount ||
          d.onboardingProfile?.typicalPerformerCount ||
          "";
        const prior =
          d.experience || d.onboardingProfile?.priorExperience || "";
        const tools = (d.onboardingProfile?.designTools ||
          []) as DrillFloOnboardingData["designTools"];
        const validTools = tools.filter((t) =>
          METHOD_OPTIONS.some((m) => m.id === t),
        ) as DrillFloOnboardingData["designTools"];
        if (d.pendingFirstDesignId) {
          pendingFirstDesignIdRef.current = d.pendingFirstDesignId;
        }
        setData((prev) => ({
          ...prev,
          userType: userType || prev.userType,
          firstName: d.firstName,
          lastName: d.lastName,
          onboardingRole: d.onboardingRole ?? prev.onboardingRole,
          typicalPerformerCount: perf || prev.typicalPerformerCount,
          priorExperience: prior || prev.priorExperience,
          designTools: validTools.length ? validTools : prev.designTools,
          showTitle: d.onboardingProfile?.firstShowTitle ?? prev.showTitle,
          showDate: d.onboardingProfile?.firstShowDate ?? prev.showDate,
        }));
      } catch (e) {
        console.error("DrillFlo onboarding: hydrate failed", e);
      } finally {
        setProfileHydrated(true);
      }
    })();
  }, [userDocId]);

  /** `AccountSetupStep` — `userType`, `onboardingRole`, optional experiment variant */
  useEffect(() => {
    if (!userDocId || !profileHydrated || !data.userType) return;
    const onboardingRole = UI_LABEL_TO_ROLE[data.userType] ?? "band_director";
    (async () => {
      try {
        await updateDoc(doc(db, "users", userDocId), {
          userType: data.userType,
          onboardingRole,
          ...(experimentVariant
            ? { onboardingExperimentVariant: experimentVariant }
            : {}),
        });
        setData((d) => ({ ...d, onboardingRole }));
      } catch (e) {
        console.error("DrillFlo onboarding: role save failed", e);
      }
    })();
  }, [userDocId, profileHydrated, data.userType, experimentVariant]);

  /** `PreferencesStep` — ensemble size */
  useEffect(() => {
    if (!userDocId || !profileHydrated || !data.typicalPerformerCount) return;
    (async () => {
      try {
        await updateDoc(doc(db, "users", userDocId), {
          typicalPerformerCount: data.typicalPerformerCount,
          "onboardingProfile.typicalPerformerCount": data.typicalPerformerCount,
        });
      } catch (e) {
        console.error("DrillFlo onboarding: ensemble size save failed", e);
      }
    })();
  }, [userDocId, profileHydrated, data.typicalPerformerCount]);

  /** `experienceStep` — prior drill software experience */
  useEffect(() => {
    if (!userDocId || !profileHydrated || !data.priorExperience) return;
    (async () => {
      try {
        await updateDoc(doc(db, "users", userDocId), {
          experience: data.priorExperience,
          "onboardingProfile.priorExperience": data.priorExperience,
        });
      } catch (e) {
        console.error("DrillFlo onboarding: experience save failed", e);
      }
    })();
  }, [userDocId, profileHydrated, data.priorExperience]);

  /** Design tools (multi-select) — `onboardingProfile.designTools` */
  useEffect(() => {
    if (!userDocId || !profileHydrated) return;
    (async () => {
      try {
        await updateDoc(doc(db, "users", userDocId), {
          "onboardingProfile.designTools": data.designTools,
        });
      } catch (e) {
        console.error("DrillFlo onboarding: design tools save failed", e);
      }
    })();
  }, [userDocId, profileHydrated, data.designTools]);

  /** First show — stored for personalization / analytics */
  useEffect(() => {
    if (!userDocId || !profileHydrated) return;
    (async () => {
      try {
        await updateDoc(doc(db, "users", userDocId), {
          "onboardingProfile.firstShowTitle": data.showTitle,
          "onboardingProfile.firstShowDate": data.showDate,
        });
        const pendingId = pendingFirstDesignIdRef.current;
        const title = data.showTitle.trim();
        if (pendingId && title) {
          await updateDoc(doc(db, "designs", pendingId), {
            title,
            updated: Timestamp.now(),
          });
        }
      } catch (e) {
        console.error("DrillFlo onboarding: show metadata save failed", e);
      }
    })();
  }, [userDocId, profileHydrated, data.showTitle, data.showDate]);

  useEffect(() => {
    if (step !== 7 || exclusiveOfferViewLogged.current) return;
    exclusiveOfferViewLogged.current = true;
    logTrialCtaShown({
      surface: "onboarding_exclusive_offer",
      flow_id: flowId,
    });
  }, [step, flowId]);

  const canGoNext = useMemo(() => {
    if (step === 0) return true;
    if (step === 1) return Boolean(data.userType);
    if (step === 2) return Boolean(data.typicalPerformerCount);
    if (step === 3) return Boolean(data.priorExperience);
    if (step === 4) return true;
    if (step === 5) return true;
    if (step === 6) return data.showTitle.trim().length > 0;
    if (step === 7) return true;
    return false;
  }, [
    step,
    data.userType,
    data.typicalPerformerCount,
    data.priorExperience,
    data.showTitle,
  ]);

  const ensureFirstOnboardingDesign = useCallback(async (): Promise<
    string | null
  > => {
    const existing = pendingFirstDesignIdRef.current;
    const title = data.showTitle.trim() || "Untitled Drill Design";
    if (existing) {
      try {
        await updateDoc(doc(db, "designs", existing), {
          title,
          updated: Timestamp.now(),
        });
      } catch (e) {
        console.error("DrillFlo onboarding: pending design title update failed", e);
      }
      return existing;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) return null;

    const userRef = doc(db, "users", userDocId || uid);
    try {
      const userSnap = await getDoc(userRef);
      const storedPending = userSnap.exists()
        ? (userSnap.data()?.pendingFirstDesignId as string | undefined)
        : undefined;
      if (storedPending) {
        setPendingId(storedPending);
        await updateDoc(doc(db, "designs", storedPending), {
          title,
          updated: Timestamp.now(),
        });
        return storedPending;
      }

      const docRef = await addDoc(collection(db, "designs"), {
        title,
        createdAt: Timestamp.now(),
        createdBy: uid,
        updated: Timestamp.now(),
      });
      setPendingId(docRef.id);
      await updateDoc(userRef, { pendingFirstDesignId: docRef.id });
      return docRef.id;
    } catch (e) {
      console.error("DrillFlo onboarding: failed to create first show", e);
      return pendingFirstDesignIdRef.current;
    }
  }, [data.showTitle, userDocId, setPendingId]);

  const checkoutUrlsForDesign = useCallback(
    (drillId: string | null) => {
      const path = drillId
        ? buildEditorUrl(drillId, {
            tutorial: true,
            onboardingRole: data.onboardingRole,
            trial: false,
            priorExperience: data.priorExperience,
            typicalPerformerCount: data.typicalPerformerCount,
          })
        : "/my-designs";
      const abs = `${window.location.origin}${path}`;
      return { successUrl: abs, cancelUrl: abs };
    },
    [data.onboardingRole, data.priorExperience, data.typicalPerformerCount],
  );

  const goNext = useCallback(async () => {
    if (!canGoNext || step >= TOTAL_STEPS - 1) return;
    if (step === 6) {
      try {
        await ensureFirstOnboardingDesign();
      } catch (e) {
        console.error("DrillFlo onboarding: create-before-trial failed", e);
      }
    }
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  }, [canGoNext, step, ensureFirstOnboardingDesign]);

  const goBack = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  useEffect(() => {
    panelRefs.current.forEach((el, i) => {
      if (!el) return;
      if (i === step) el.removeAttribute("inert");
      else el.setAttribute("inert", "");
    });
  }, [step]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const root = panelRefs.current[step];
      const focusable = root?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }, 180);
    return () => window.clearTimeout(t);
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        if (step < TOTAL_STEPS - 1) {
          e.preventDefault();
          goNext();
        }
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        if (step > 0) {
          e.preventDefault();
          goBack();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, goNext, goBack]);

  const toggleDesignTool = (
    id: DrillFloOnboardingData["designTools"][number],
  ) => {
    setData((d) => {
      const has = d.designTools.includes(id);
      return {
        ...d,
        designTools: has
          ? d.designTools.filter((m) => m !== id)
          : [...d.designTools, id],
      };
    });
  };

  const completeOnboarding = useCallback(
    async (mode: "template" | "blank") => {
      const nextData: DrillFloOnboardingData = { ...data, entryMode: mode };
      setData(nextData);

      logEvent(analytics, "complete_account_setup", {
        user: auth.currentUser?.uid,
        flow_id: flowId,
        experiment_name: experimentName,
        experiment_variant: experimentVariant ?? "",
        fast_path: fastPath,
      });

      const uid = auth.currentUser?.uid;
      if (!uid) {
        navigate("/login");
        return;
      }

      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};
      const onboardingRole = (userData.onboardingRole ||
        null) as OnboardingRole | null;

      const plan = await getUserPlanData(uid);
      const showTrialCta = !plan.planActive;

      const finalizeUserOnboarding = async () => {
        const ref = doc(db, "users", uid);
        const userDoc = await getDoc(ref);
        if (!userDoc.exists()) return;
        const ud = userDoc.data();
        const currentChecklist = ud.onboardingChecklist || {};
        await updateDoc(ref, {
          onboarded: "Y",
          onboardingWizardCompletedAt: serverTimestamp(),
          onboardingPathId: flowId,
          onboardingExperimentName: experimentName,
          onboardingExperimentVariant: experimentVariant ?? "",
          onboardingFastPath: fastPath,
          onboardingDeferredProfilingPending: fastPath === true,
          onboardingChecklist: {
            ...currentChecklist,
            "create-drill": true,
          },
        });
      };

      const editorOnboardingDone =
        typeof localStorage !== "undefined" &&
        localStorage.getItem(EDITOR_ONBOARDING_STORAGE_COMPLETED) === "true";

      const drillTitle = data.showTitle.trim() || "Untitled Drill Design";

      const navigateToDrill = (
        drillId: string,
        fromTemplate: boolean,
        hadNoDrillsForTutorial: boolean,
        analyticsDrillTitle: string,
      ) => {
        logActivationDrillCreated({
          flow_id: flowId,
          document_id: drillId,
          from_template: fromTemplate,
          onboarding_role: onboardingRole,
        });
        logEvent(analytics, "create_new_drill_document", {
          drill_title: analyticsDrillTitle,
          created_by: uid,
          created_at: Timestamp.now().toDate().toISOString(),
          document_id: drillId,
          device_type: navigator.userAgent.includes("Mobi")
            ? "mobile"
            : "desktop",
        });
        const tutorial = hadNoDrillsForTutorial && !editorOnboardingDone;
        navigate(
          buildEditorUrl(drillId, {
            tutorial,
            onboardingRole: onboardingRole || null,
            trial: showTrialCta,
            priorExperience: data.priorExperience,
            typicalPerformerCount: data.typicalPerformerCount,
          }),
        );
      };

      const runBlankDrill = async () => {
        const pendingId =
          pendingFirstDesignIdRef.current ||
          (userData.pendingFirstDesignId as string | undefined);
        if (pendingId) {
          await updateDoc(doc(db, "designs", pendingId), {
            title: drillTitle,
            updated: Timestamp.now(),
          });
          await finalizeUserOnboarding();
          onComplete?.(nextData);
          navigateToDrill(pendingId, false, true, drillTitle);
          return;
        }

        const designsQuery = query(
          collection(db, "designs"),
          where("createdBy", "==", uid),
        );
        const existing = await getDocs(designsQuery);
        const hadNoDrills = existing.empty;

        const newDrill = {
          title: drillTitle,
          createdAt: Timestamp.now(),
          createdBy: uid,
          updated: Timestamp.now(),
        };
        const docRef = await addDoc(collection(db, "designs"), newDrill);

        await finalizeUserOnboarding();
        onComplete?.(nextData);
        navigateToDrill(docRef.id, false, hadNoDrills, drillTitle);
      };

      if (mode === "template") {
        await ensureRemoteConfig();
        const redirectParam = searchParams.get("redirect");
        let templateId: string | null = null;
        if (redirectParam) {
          templateId = templateIdFromTemplatesPath(redirectParam);
        }
        if (!templateId && typeof sessionStorage !== "undefined") {
          templateId = sessionStorage.getItem(PENDING_TEMPLATE_STORAGE_KEY);
        }
        if (!templateId) {
          const roleTemplate = getDefaultTemplateIdForRole(
            remoteConfig,
            onboardingRole,
          );
          templateId = roleTemplate || null;
        }

        if (templateId) {
          setIsFinishing(true);
          try {
            if (typeof sessionStorage !== "undefined") {
              sessionStorage.removeItem(PENDING_TEMPLATE_STORAGE_KEY);
            }
            const newId = await createDesignFromTemplate(
              templateId,
              uid,
              drillTitle,
            );
            await finalizeUserOnboarding();
            onComplete?.(nextData);
            navigateToDrill(newId, true, true, "from_template");
          } catch (e) {
            console.error(
              "Error creating drill from template after onboarding:",
              e,
            );
            navigate("/my-designs");
          } finally {
            setIsFinishing(false);
          }
          return;
        }
      }

      setIsFinishing(true);
      try {
        await runBlankDrill();
      } catch (e) {
        console.error("Error creating drill after onboarding:", e);
        navigate("/my-designs");
      } finally {
        setIsFinishing(false);
      }
    },
    [
      data,
      navigate,
      searchParams,
      flowId,
      experimentName,
      experimentVariant,
      fastPath,
      onComplete,
    ],
  );

  const stagger = (i: number) =>
    ({ animationDelay: `${i * 60}ms` }) as React.CSSProperties;

  return (
    <div
      className={cn(
        "font-geist relative min-h-[100dvh] w-full bg-flo-canvas text-slate-900 antialiased",
        className,
      )}
    >
      <style dangerouslySetInnerHTML={{ __html: ONBOARDING_STYLES }} />

      <div
        className="relative z-10 flex min-h-[100dvh] flex-col"
        role="region"
        aria-roledescription="Onboarding"
        aria-label="DrillFlo onboarding"
        id={stepRegionId}
      >
        <header className="flex shrink-0 items-center justify-between px-4 pb-2 pt-4 sm:px-8">
          <div className="font-fraunces text-lg font-semibold tracking-tight text-slate-800">
            Drill<span className="text-flo-blue">Flo</span>
          </div>
          <nav
            className="flex items-center gap-2"
            aria-label="Onboarding progress"
          >
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  "block h-2 rounded-full bg-slate-300/80 transition-all duration-300 ease-out",
                  i === step ? "w-7 bg-flo-blue" : "w-2",
                )}
                aria-current={i === step ? "step" : undefined}
              />
            ))}
          </nav>
        </header>

        <div className="relative min-h-0 flex-1 overflow-hidden px-0">
          <div
            className="flex h-full min-h-[min(560px,75vh)] transition-transform duration-150 ease-out will-change-transform"
            style={{
              width: `${TOTAL_STEPS * 100}%`,
              transform: `translateX(-${step * STEP_PANEL_WIDTH_PCT}%)`,
            }}
          >
            {/* 1 — Welcome */}
            <section
              ref={(el) => setPanelRef(el, 0)}
              style={{ width: `${STEP_PANEL_WIDTH_PCT}%` }}
              className="relative flex shrink-0 flex-col px-5 pb-8 pt-4 sm:px-10"
              aria-label="Welcome"
              aria-hidden={step !== 0}
            >
              <FieldGridBackdrop />
              <div className="relative z-[1] mx-auto flex max-w-lg flex-1 flex-col items-center justify-center text-center">
                <div className="flo-enter-item mb-6" style={stagger(0)}>
                  <Wordmark reveal />
                </div>
                <p
                  className="flo-enter-item font-fraunces text-xl font-medium text-slate-600 sm:text-2xl"
                  style={stagger(1)}
                >
                  Drill design, reimagined.
                </p>
                <p
                  className=" pt-8 flo-enter-item font-fraunces text-xl font-medium text-slate-600 sm:text-2xl"
                  style={stagger(2)}
                >
                  {data?.firstName
                    ? `${data.firstName.trim().charAt(0).toUpperCase() + data.firstName.trim().slice(1)} let's get your account set up.`
                    : "Let's get your account set up."}
                </p>
                <div className="mt-14 w-full max-w-xs" style={stagger(2)}>
                  <button
                    type="button"
                    onClick={goNext}
                    className="flo-btn-primary flo-enter-item w-full rounded-xl px-6 py-4 text-base font-semibold shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2 focus-visible:ring-offset-flo-canvas"
                  >
                    Get started →
                  </button>
                </div>
              </div>
            </section>

            {/* 2 — Role (AccountSetupStep) */}
            <section
              ref={(el) => setPanelRef(el, 1)}
              style={{ width: `${STEP_PANEL_WIDTH_PCT}%` }}
              className="flex shrink-0 flex-col justify-center px-5 pb-10 pt-2 sm:px-10"
              aria-label="Tell us about yourself"
              aria-hidden={step !== 1}
            >
              <div className="mx-auto w-full max-w-xl">
                <h1
                  className="flo-enter-item font-fraunces text-3xl font-semibold tracking-tight text-slate-900 sm:text-[2rem]"
                  style={stagger(0)}
                >
                  Tell us about yourself
                  {data.firstName
                    ? `, ${data.firstName.trim().charAt(0).toUpperCase() + data.firstName.trim().slice(1)}.`
                    : ""}
                </h1>
                <div
                  className="flo-enter-item mt-8 space-y-3"
                  style={stagger(1)}
                >
                  <span className="font-geist text-sm font-medium text-slate-700">
                    I am a…
                  </span>
                  <div className="grid grid-cols-2 gap-4">
                    {ROLE_UI_OPTIONS.map((type, i) => {
                      const selected = data.userType === type;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() =>
                            setData((d) => ({ ...d, userType: type }))
                          }
                          aria-pressed={selected}
                          aria-label={type}
                          className={cn(
                            "flo-enter-item flo-card-tile flex items-center justify-center gap-2 rounded-2xl border-2 px-3 py-4 text-center font-geist text-sm font-medium transition-colors duration-150",
                            selected
                              ? "border-flo-blue bg-flo-blue-tint text-flo-blue"
                              : "border-slate-200/90 bg-white text-slate-800 hover:border-slate-300",
                          )}
                          style={stagger(2 + i)}
                        >
                          <IconUser
                            className={cn(
                              "h-5 w-5 shrink-0",
                              selected ? "text-flo-blue" : "text-slate-500",
                            )}
                          />
                          {type}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* 3 — Ensemble size (PreferencesStep) */}
            <section
              ref={(el) => setPanelRef(el, 2)}
              style={{ width: `${STEP_PANEL_WIDTH_PCT}%` }}
              className="flex shrink-0 flex-col justify-center px-5 pb-10 pt-2 sm:px-10"
              aria-label="What size ensemble"
              aria-hidden={step !== 2}
            >
              <div className="mx-auto w-full max-w-xl">
                <h1
                  className="flo-enter-item font-fraunces text-3xl font-semibold tracking-tight text-slate-900 sm:text-[2rem]"
                  style={stagger(0)}
                >
                  What size ensemble?
                </h1>
                <div
                  className="flo-enter-item mt-8 space-y-3"
                  style={stagger(1)}
                >
                  <span className="font-geist text-sm font-medium text-slate-700">
                    Typical performer count
                  </span>
                  <div className="grid grid-cols-2 gap-4">
                    {PERFORMER_OPTIONS.map((count, i) => {
                      const selected = data.typicalPerformerCount === count;
                      return (
                        <button
                          key={count}
                          type="button"
                          onClick={() =>
                            setData((d) => ({
                              ...d,
                              typicalPerformerCount: count,
                            }))
                          }
                          aria-pressed={selected}
                          className={cn(
                            "flo-enter-item flo-card-tile flex items-center justify-center gap-2 rounded-2xl border-2 px-3 py-4 text-center font-geist text-sm font-medium transition-colors duration-150",
                            selected
                              ? "border-flo-blue bg-flo-blue-tint text-flo-blue"
                              : "border-slate-200/90 bg-white text-slate-800 hover:border-slate-300",
                          )}
                          style={stagger(2 + i)}
                        >
                          <IconUsersEnsemble
                            className={cn(
                              "h-5 w-5 shrink-0",
                              selected ? "text-flo-blue" : "text-slate-500",
                            )}
                          />
                          {count}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* 4 — Prior software (experienceStep) */}
            <section
              ref={(el) => setPanelRef(el, 3)}
              style={{ width: `${STEP_PANEL_WIDTH_PCT}%` }}
              className="flex shrink-0 flex-col justify-center px-5 pb-10 pt-2 sm:px-10"
              aria-label="Have you used drill design software before"
              aria-hidden={step !== 3}
            >
              <div className="mx-auto w-full max-w-xl">
                <h1
                  className="flo-enter-item font-fraunces text-3xl font-semibold tracking-tight text-slate-900 sm:text-[2rem]"
                  style={stagger(0)}
                >
                  Have you used drill design software before?
                </h1>
                <div
                  className="flo-enter-item mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2"
                  style={stagger(1)}
                >
                  {PRIOR_SOFTWARE_OPTIONS.map((label, i) => {
                    const selected = data.priorExperience === label;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() =>
                          setData((d) => ({ ...d, priorExperience: label }))
                        }
                        aria-pressed={selected}
                        className={cn(
                          "flo-enter-item flo-card-tile flex items-center justify-center gap-2 rounded-2xl border-2 px-4 py-4 text-center font-geist text-sm font-medium transition-colors duration-150",
                          selected
                            ? "border-flo-blue bg-flo-blue-tint text-flo-blue"
                            : "border-slate-200/90 bg-white text-slate-800 hover:border-slate-300",
                        )}
                        style={stagger(2 + i)}
                      >
                        {label === "Yes, I have" ? (
                          <IconCheck
                            className={cn(
                              "h-5 w-5",
                              selected ? "text-flo-blue" : "text-slate-500",
                            )}
                          />
                        ) : (
                          <IconCircleOff
                            className={cn(
                              "h-5 w-5",
                              selected ? "text-flo-blue" : "text-slate-500",
                            )}
                          />
                        )}
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* 5 — Design tools */}
            <section
              ref={(el) => setPanelRef(el, 4)}
              style={{ width: `${STEP_PANEL_WIDTH_PCT}%` }}
              className="flex shrink-0 flex-col justify-center px-5 pb-10 pt-2 sm:px-10"
              aria-label="How do you currently design"
              aria-hidden={step !== 4}
            >
              <div className="mx-auto w-full max-w-xl">
                <h1
                  className="flo-enter-item font-fraunces text-3xl font-semibold tracking-tight text-slate-900 sm:text-[2rem]"
                  style={stagger(0)}
                >
                  How do you currently design?
                </h1>
                <p
                  className="flo-enter-item mt-3 text-base text-slate-600"
                  style={stagger(1)}
                >
                  So we can tailor your experience — select all that apply.
                </p>
                <div className="mt-10 flex flex-wrap gap-3">
                  {METHOD_OPTIONS.map((m, i) => {
                    const on = data.designTools.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleDesignTool(m.id)}
                        aria-pressed={on}
                        className={cn(
                          "flo-enter-item flo-chip rounded-full border px-5 py-3 text-sm font-medium transition duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2 focus-visible:ring-offset-flo-canvas",
                          on
                            ? "border-flo-blue bg-flo-blue-tint text-flo-blue"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300",
                        )}
                        style={stagger(2 + i)}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* 6 — Flo */}
            <section
              ref={(el) => setPanelRef(el, 5)}
              style={{ width: `${STEP_PANEL_WIDTH_PCT}%` }}
              className="flex shrink-0 flex-col justify-center px-5 pb-10 pt-2 sm:px-10"
              aria-label="Welome to DrillFlo"
              aria-hidden={step !== 5}
            >
              <div className="mx-auto grid w-full max-w-4xl gap-10 lg:grid-cols-2 lg:items-center">
                <div className="flo-enter-item" style={stagger(0)}>
                  <FloAvatar />
                  <p className="mt-6 text-center font-fraunces text-2xl font-semibold text-slate-900 lg:text-left">
                    Meet DrillFlo
                  </p>
                  <p className="mt-2 text-center text-sm text-slate-600 lg:text-left">
                    The first marching band drill software that puts you in
                    control. DrillFlo is designed to be easy to use, fast, and
                    collaborative.
                  </p>
                </div>
                <div>
                  <h1
                    className="flo-enter-item font-fraunces text-3xl font-semibold tracking-tight text-slate-900 sm:text-[2rem]"
                    style={stagger(1)}
                  >
                    Anywhere you go, DrillFlo goes with you.
                  </h1>
                  <ul className="mt-8 space-y-4">
                    {[
                      "Click and drag to design your drill",
                      "Sync to your music",
                      "Collaborate with your team",
                      "Exports production-ready PDFs",
                    ].map((text, i) => (
                      <li
                        key={text}
                        className="flo-enter-item flex items-center gap-3 rounded-2xl border border-slate-200/90 bg-white px-4 py-3 text-sm font-medium text-slate-800 shadow-sm"
                        style={stagger(2 + i)}
                      >
                        <span
                          className="font-geist-mono flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-flo-blue-tint text-xs text-flo-blue"
                          aria-hidden
                        >
                          {i + 1}
                        </span>
                        {text}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-10">
                    <button
                      type="button"
                      onClick={goNext}
                      className="flo-btn-primary flo-enter-item w-full rounded-xl px-6 py-4 text-base font-semibold shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2 sm:w-auto"
                      style={stagger(5)}
                    >
                      Let&apos;s start designing →
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* 7 — Show */}
            <section
              ref={(el) => setPanelRef(el, 6)}
              style={{ width: `${STEP_PANEL_WIDTH_PCT}%` }}
              className="flex shrink-0 flex-col justify-center px-5 pb-10 pt-2 sm:px-10"
              aria-label="Name your first show"
              aria-hidden={step !== 6}
            >
              <div className="mx-auto w-full max-w-md">
                <h1
                  className="flo-enter-item font-fraunces text-3xl font-semibold tracking-tight text-slate-900"
                  style={stagger(0)}
                >
                  Name your first show
                </h1>
                <p
                  className="flo-enter-item mt-3 text-slate-600"
                  style={stagger(1)}
                >
                  You can always change this later.
                </p>
                <div className="mt-10 space-y-8">
                  <div className="flo-enter-item" style={stagger(2)}>
                    <label
                      htmlFor="show-title"
                      className="font-fraunces text-lg font-medium text-slate-800"
                    >
                      Show title
                    </label>
                    <input
                      id="show-title"
                      type="text"
                      autoComplete="off"
                      value={data.showTitle}
                      onChange={(e) =>
                        setData((d) => ({ ...d, showTitle: e.target.value }))
                      }
                      className="flo-input-focus mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-4 font-geist text-lg text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400"
                      placeholder="e.g. Homecoming 2026"
                      aria-required
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* 8 — Exclusive launch offer */}
            <section
              ref={(el) => setPanelRef(el, 7)}
              style={{ width: `${STEP_PANEL_WIDTH_PCT}%` }}
              className="flex shrink-0 flex-col justify-center overflow-y-auto px-5 pb-10 pt-2 sm:px-10"
              aria-label="Exclusive onboarding offer"
              aria-hidden={step !== 7}
            >
              <div className="mx-auto w-full max-w-lg">
                <div
                  className="flo-enter-item mb-4 flex justify-center sm:justify-start"
                  style={stagger(0)}
                >
                  <span className="inline-flex items-center gap-2 rounded-full border border-green-200/90 bg-green-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-green-900">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden />
                    One-time exclusive offer
                  </span>
                </div>
                <div className="mb-6 flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
                  <h1
                    className="flo-enter-item text-center font-fraunces text-3xl font-semibold tracking-tight text-slate-900 sm:text-left sm:text-[2rem]"
                    style={stagger(1)}
                  >
                    Start free — then save{" "}
                    {
                      process.env
                        .REACT_APP_STRIPE_WELCOME_OFFER_DISCOUNT_PERCENTAGE
                    }
                    % on your first 6 months.
                  </h1>
                </div>
                <p
                  className="flo-enter-item mt-4 text-center text-sm text-slate-600 sm:text-left"
                  style={stagger(5)}
                >
                  <span className="font-fraunces text-2xl font-semibold tracking-tight text-slate-900">
                    {
                      process.env
                        .REACT_APP_STRIPE_WELCOME_OFFER_DISCOUNT_PERCENTAGE
                    }
                    % off your first 6 months
                  </span>
                </p>
                <p
                  className="flo-enter-item text-center text-base text-slate-600 sm:text-left"
                  style={stagger(2)}
                >
                  As a welcome to DrillFlo, begin your {TRIAL_LENGTH}-day
                  DrillFlo Pro trial, AND save{" "}
                  {
                    process.env
                      .REACT_APP_STRIPE_WELCOME_OFFER_DISCOUNT_PERCENTAGE
                  }
                  % on each of your first six paid months when you subscribe.
                </p>
                <div
                  className="flo-enter-item mt-8 rounded-2xl border border-slate-200/90 bg-flo-blue-tint p-5"
                  style={stagger(3)}
                >
                  <ul className="space-y-2.5 text-sm text-slate-700">
                    {[
                      `Full Pro access during your ${TRIAL_LENGTH}-day trial — cancel anytime before it ends`,
                      `${process.env.REACT_APP_STRIPE_WELCOME_OFFER_DISCOUNT_PERCENTAGE}% off the regular rate for your first six billing months after the trial`,
                      "Discount is applied at checkout when you use this offer",
                    ].map((text) => (
                      <li key={text} className="flex items-start gap-2">
                        <Check
                          className="mt-0.5 h-4 w-4 shrink-0 text-flo-blue"
                          strokeWidth={2.5}
                          aria-hidden
                        />
                        {text}
                      </li>
                    ))}
                  </ul>
                </div>
                <div
                  className="flo-enter-item mt-8 flex flex-wrap justify-center gap-2 sm:justify-start"
                  style={stagger(4)}
                >
                  {(["monthly", "annual"] as const).map((cycle) => (
                    <button
                      key={cycle}
                      type="button"
                      className={cn(
                        "rounded-full border-2 px-5 py-2.5 text-sm font-medium transition duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2",
                        offerBilling === cycle
                          ? "border-flo-blue bg-flo-blue-tint text-flo-blue"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300",
                      )}
                      onClick={() => setOfferBilling(cycle)}
                    >
                      {cycle === "monthly" ? "Monthly" : "Annual"}
                      {cycle === "annual" ? (
                        <span className="ml-2 rounded-full bg-flo-blue-tint px-2 py-0.5 text-xs font-normal text-flo-blue">
                          Save 17%
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
                <p
                  className="flo-enter-item mt-4 text-center text-sm text-slate-600 sm:text-left"
                  style={stagger(5)}
                >
                  <span
                    style={{ textDecoration: "line-through" }}
                    className="font-fraunces text-2xl font-semibold tracking-tight text-slate-900 slash-through"
                  >
                    {offerBilling === "monthly" ? "$25" : "$250"}
                  </span>
                  <span className="font-fraunces ml-2 text-3xl font-semibold tracking-tight text-blue-700">
                    {offerBilling === "monthly"
                      ? `$${(25 * (1 - Number(process.env.REACT_APP_STRIPE_WELCOME_OFFER_DISCOUNT_PERCENTAGE) / 100)).toFixed(2)}`
                      : `$${(250 * (1 - Number(process.env.REACT_APP_STRIPE_WELCOME_OFFER_DISCOUNT_PERCENTAGE) / 100)).toFixed(2)}`}
                  </span>
                  <span className="text-base font-normal text-slate-600">
                    {" "}
                    /{offerBilling === "monthly" ? "mo" : "yr"}
                  </span>
                </p>

                <p
                  className="flo-enter-item mt-4 text-center text-sm text-slate-600 sm:text-left"
                  style={stagger(5)}
                >
                  <span className="font-fraunces text-2xl font-semibold tracking-tight text-slate-900"></span>
                </p>
                <div className="flo-enter-item mt-2" style={stagger(6)}>
                  {user?.uid ? (
                    <SubscriptionButton
                      priceId={STRIPE_CONFIG.prices.professional[offerBilling]}
                      userId={user.uid}
                      planName={BILLING.paidPlanName}
                      isEducational={false}
                      trialLength={TRIAL_LENGTH}
                      alreadyTrialed={false}
                      promotionCodeId={
                        STRIPE_CONFIG.welcomePromotionCodeId || null
                      }
                      onBeforeCheckout={async () => {
                        const id = await ensureFirstOnboardingDesign();
                        const uid = auth.currentUser?.uid;
                        const userRefId = userDocId || uid;
                        if (userRefId) {
                          try {
                            await updateDoc(doc(db, "users", userRefId), {
                              onboarded: "Y",
                            });
                          } catch (e) {
                            console.error(
                              "DrillFlo onboarding: failed to mark onboarded before checkout",
                              e,
                            );
                          }
                        }
                        return checkoutUrlsForDesign(id);
                      }}
                    />
                  ) : (
                    <p className="text-center text-sm text-slate-500">
                      Preparing checkout…
                    </p>
                  )}
                </div>
                <p
                  className="flo-enter-item mt-4 text-center text-xs leading-relaxed text-slate-500"
                  style={stagger(7)}
                >
                  A credit card is required to start the DrillFlo Pro trial.
                  You won&apos;t be charged until the trial ends. You can
                  continue below to open the editor first — this offer
                  won&apos;t appear again in onboarding.
                </p>
              </div>
            </section>

            {/* 9 — Done */}
            <section
              ref={(el) => setPanelRef(el, 8)}
              style={{ width: `${STEP_PANEL_WIDTH_PCT}%` }}
              className="flex shrink-0 flex-col justify-center px-5 pb-12 pt-2 sm:px-10"
              aria-label="You are in"
              aria-hidden={step !== 8}
            >
              <div className="mx-auto w-full max-w-xl text-center">
                <div
                  className="flo-spring-mark mx-auto mb-8 inline-flex items-center justify-center"
                  aria-hidden
                >
                  <div className="font-fraunces text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
                    Drill<span className="text-flo-blue">Flo</span>
                  </div>
                </div>
                <h1 className="flo-enter-item font-fraunces text-[1.65rem] font-semibold leading-snug text-slate-900 sm:text-3xl">
                  Welcome to DrillFlo{data.firstName ? `, ` : ""}
                  <span className="text-slate-800">
                    {data.firstName
                      ? `${data.firstName.trim().charAt(0).toUpperCase() + data.firstName.trim().slice(1)}`
                      : ""}
                  </span>
                  .
                </h1>
                <p
                  className="flo-enter-item mt-4 text-slate-600"
                  style={stagger(1)}
                >
                  Let's get started on your first show
                  {data.showTitle
                    ? `, ${data.showTitle.trim().charAt(0).toUpperCase() + data.showTitle.trim().slice(1)}`
                    : ""}
                  .
                </p>
                <div className="mt-12 grid gap-4 sm:grid-cols-1">
                  <button
                    type="button"
                    disabled={isFinishing}
                    onClick={() => void completeOnboarding("template")}
                    className="hidden flo-enter-item flo-card-tile rounded-2xl border-2 border-slate-200 bg-white p-6 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60"
                    style={stagger(2)}
                  >
                    <span className="font-fraunces text-xl font-semibold text-slate-900">
                      {isFinishing
                        ? "Opening editor…"
                        : "Start from a template"}
                    </span>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                      Hit the ground with a ready-made skeleton and adapt fast.
                    </p>
                  </button>
                  <button
                    type="button"
                    disabled={isFinishing}
                    onClick={() => void completeOnboarding("blank")}
                    className="flo-enter-item flo-card-tile rounded-2xl border-2 border-slate-200 bg-white p-6 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60"
                    style={stagger(3)}
                  >
                    <span className="font-fraunces text-xl font-semibold text-slate-900">
                      {isFinishing
                        ? "Opening editor…"
                        : "Begin with a blank field"}
                    </span>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                      Open canvas for your ensemble, your story arc.
                    </p>
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>

        {step > 0 && step < TOTAL_STEPS - 1 && (
          <footer className="shrink-0 border-t border-slate-200/80 bg-flo-canvas/95 px-5 py-4 backdrop-blur-sm sm:px-10">
            <div className="mx-auto flex max-w-4xl items-center justify-between gap-4">
              <button
                type="button"
                onClick={goBack}
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2"
              >
                Back
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={!canGoNext}
                className={cn(
                  "rounded-xl px-6 py-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2",
                  canGoNext
                    ? "flo-btn-primary text-white shadow-sm"
                    : "cursor-not-allowed bg-slate-200 text-slate-500",
                )}
              >
                Continue
              </button>
            </div>
          </footer>
        )}
      </div>

      <GlobalOnboardingUtilities />
    </div>
  );
}

/** Shared button + card motion (Tailwind + a few globals). */
function GlobalOnboardingUtilities() {
  return (
    <style>{`
      .flo-btn-primary {
        background-color: ${FLO_BLUE};
        color: white;
      }
      .flo-btn-primary:hover:not(:disabled) {
        background-color: ${FLO_BLUE_HOVER};
      }
      .flo-btn-primary:active:not(:disabled) {
        transform: scale(0.97);
      }
      .flo-card-tile {
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
      }
      .flo-card-tile:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 28px rgba(15, 23, 42, 0.1);
      }
      .flo-chip:hover:not(:disabled) {
        transform: translateY(-1px);
      }
      .flo-input-focus:focus {
        border-color: ${FLO_BLUE};
        box-shadow: 0 0 0 3px rgba(46, 91, 255, 0.22);
        animation: flo-focus-pulse 2.4s ease-in-out infinite;
      }
      @keyframes flo-focus-pulse {
        0%, 100% { box-shadow: 0 0 0 3px rgba(46, 91, 255, 0.22); }
        50% { box-shadow: 0 0 0 5px rgba(46, 91, 255, 0.12); }
      }
      @media (prefers-reduced-motion: reduce) {
        .flo-btn-primary:active:not(:disabled) { transform: none; }
        .flo-card-tile:hover { transform: none; }
        .flo-input-focus:focus { animation: none; }
      }
    `}</style>
  );
}

export default DrillFloOnboardingExperience;
